import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/Card';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import PasswordInput from '../ui/PasswordInput';
import { useAuth } from '../../contexts/AuthContext';

const ProfilEmployeTab = () => {
  const { token, user } = useAuth();
  const [profileData, setProfileData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPinForm, setShowPinForm] = useState(false);
  const [pinForm, setPinForm] = useState({
    current_pin: '',
    new_pin: '',
    confirm_pin: ''
  });
  const [pinError, setPinError] = useState('');
  const [pinSuccess, setPinSuccess] = useState('');

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/employe/profile`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setProfileData(data);
      } else {
        console.error('Erreur lors du chargement du profil');
      }
    } catch (error) {
      console.error('Erreur:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatAmount = (amount) => {
    return new Intl.NumberFormat('fr-GN', {
      style: 'currency',
      currency: 'GNF',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const getStatusBadge = (statut) => {
    const statusConfig = {
      'ACTIF': { color: 'bg-green-100 text-green-800', text: 'Actif' },
      'INACTIF': { color: 'bg-gray-100 text-gray-800', text: 'Inactif' },
      'SUSPENDU': { color: 'bg-red-100 text-red-800', text: 'Suspendu' }
    };

    const config = statusConfig[statut] || { color: 'bg-gray-100 text-gray-800', text: statut };
    
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-medium ${config.color}`}>
        {config.text}
      </span>
    );
  };

  const getContractBadge = (type) => {
    const contractConfig = {
      'CDI': { color: 'bg-blue-100 text-blue-800', text: 'CDI' },
      'CDD': { color: 'bg-yellow-100 text-yellow-800', text: 'CDD' },
      'STAGE': { color: 'bg-purple-100 text-purple-800', text: 'Stage' },
      'CONSULTANT': { color: 'bg-indigo-100 text-indigo-800', text: 'Consultant' }
    };

    const config = contractConfig[type] || { color: 'bg-gray-100 text-gray-800', text: type };
    
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-medium ${config.color}`}>
        {config.text}
      </span>
    );
  };

  const handlePinChange = async (e) => {
    e.preventDefault();
    setPinError('');
    setPinSuccess('');

    if (pinForm.new_pin !== pinForm.confirm_pin) {
      setPinError('Les nouveaux PIN ne correspondent pas');
      return;
    }

    if (pinForm.new_pin.length !== 4 || !/^\d{4}$/.test(pinForm.new_pin)) {
      setPinError('Le PIN doit contenir exactement 4 chiffres');
      return;
    }

    try {
      // Ici vous pouvez ajouter l'API pour changer le PIN
      // Pour l'instant, on simule le succès
      setPinSuccess('PIN modifié avec succès');
      setPinForm({ current_pin: '', new_pin: '', confirm_pin: '' });
      setShowPinForm(false);
      setTimeout(() => setPinSuccess(''), 3000);
    } catch (error) {
      setPinError('Erreur lors de la modification du PIN');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Chargement de votre profil...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Messages */}
      {pinSuccess && (
        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded">
          ✅ {pinSuccess}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Informations personnelles */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <span className="mr-2">👤</span>
              Informations personnelles
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center space-x-4">
                <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center">
                  <span className="text-white font-bold text-xl">
                    {profileData?.nom?.charAt(0) || 'E'}
                  </span>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    {profileData?.nom} {profileData?.prenom}
                  </h3>
                  <p className="text-gray-600">Matricule: {profileData?.matricule}</p>
                  <div className="flex items-center space-x-2 mt-1">
                    {getStatusBadge(profileData?.statut)}
                    {getContractBadge(profileData?.contrat_type)}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Téléphone</label>
                  <p className="mt-1 text-sm text-gray-900">{profileData?.telephone}</p>
                </div>
                
                {profileData?.email && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Email</label>
                    <p className="mt-1 text-sm text-gray-900">{profileData.email}</p>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700">Date d'embauche</label>
                  <p className="mt-1 text-sm text-gray-900">{formatDate(profileData?.date_embauche)}</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Salaire net mensuel</label>
                  <p className="mt-1 text-sm font-semibold text-green-600">
                    {formatAmount(profileData?.salaire_net)}
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Sécurité */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span className="flex items-center">
                <span className="mr-2">🔒</span>
                Sécurité
              </span>
              {!showPinForm && (
                <Button 
                  size="sm" 
                  onClick={() => setShowPinForm(true)}
                >
                  Changer PIN
                </Button>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {showPinForm ? (
              <form onSubmit={handlePinChange} className="space-y-4">
                {pinError && (
                  <div className="bg-red-100 border border-red-400 text-red-700 px-3 py-2 rounded text-sm">
                    {pinError}
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    PIN actuel
                  </label>
                  <PasswordInput
                    value={pinForm.current_pin}
                    onChange={(e) => setPinForm({...pinForm, current_pin: e.target.value})}
                    maxLength="4"
                    placeholder="••••"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nouveau PIN
                  </label>
                  <PasswordInput
                    value={pinForm.new_pin}
                    onChange={(e) => setPinForm({...pinForm, new_pin: e.target.value})}
                    maxLength="4"
                    placeholder="••••"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Confirmer le nouveau PIN
                  </label>
                  <PasswordInput
                    value={pinForm.confirm_pin}
                    onChange={(e) => setPinForm({...pinForm, confirm_pin: e.target.value})}
                    maxLength="4"
                    placeholder="••••"
                    required
                  />
                </div>

                <div className="flex space-x-3">
                  <Button type="submit" size="sm">
                    Modifier
                  </Button>
                  <Button 
                    type="button" 
                    variant="outline" 
                    size="sm"
                    onClick={() => {
                      setShowPinForm(false);
                      setPinForm({ current_pin: '', new_pin: '', confirm_pin: '' });
                      setPinError('');
                    }}
                  >
                    Annuler
                  </Button>
                </div>
              </form>
            ) : (
              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                    <span className="text-green-600">🔐</span>
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">PIN de connexion</p>
                    <p className="text-sm text-gray-600">PIN à 4 chiffres configuré</p>
                  </div>
                </div>

                <div className="bg-blue-50 rounded-lg p-4">
                  <div className="flex items-start space-x-3">
                    <span className="text-blue-600 text-lg">💡</span>
                    <div>
                      <h4 className="font-medium text-blue-900">Conseils de sécurité</h4>
                      <ul className="text-sm text-blue-800 mt-2 space-y-1">
                        <li>• Changez votre PIN régulièrement</li>
                        <li>• Ne partagez jamais votre PIN</li>
                        <li>• Utilisez un PIN difficile à deviner</li>
                        <li>• Évitez les suites simples (1234, 0000)</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Informations sur l'entreprise */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <span className="mr-2">🏢</span>
            Mon entreprise
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="text-center">
              <div className="text-4xl mb-2">🏢</div>
              <p className="text-gray-600">
                Informations sur votre entreprise disponibles bientôt
              </p>
              <p className="text-sm text-gray-500 mt-1">
                Nous ajoutons cette fonctionnalité dans une prochaine mise à jour
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Support */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <span className="mr-2">🆘</span>
            Besoin d'aide ?
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-blue-50 rounded-lg p-4">
              <div className="flex items-center space-x-3">
                <span className="text-blue-600 text-2xl">💬</span>
                <div>
                  <h4 className="font-medium text-blue-900">Support technique</h4>
                  <p className="text-sm text-blue-700">
                    Contactez notre équipe support pour toute question technique
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-green-50 rounded-lg p-4">
              <div className="flex items-center space-x-3">
                <span className="text-green-600 text-2xl">👥</span>
                <div>
                  <h4 className="font-medium text-green-900">Ressources Humaines</h4>
                  <p className="text-sm text-green-700">
                    Pour les questions sur votre contrat ou vos avances
                  </p>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ProfilEmployeTab;